import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { IoCheckmark, IoOpenOutline, IoDocumentTextOutline } from 'react-icons/io5';
import styled from 'styled-components';
import { ErrorText } from '../../../components/utils/AvatarCropper';
import { Palette } from '../../../components/utils';
import { InputSeparator } from '../../settings/SettingsModals';
import { FieldLabel } from '../../../components/WizardV2/wizardStylesV2';
import {
  ScheduleDayToggleButton,
  ScheduleDayToggleMark
} from '../../properties/Unit&Amenities/Amenities/V2/addAmenityWizardStylesV2';

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 540px;
  height: 100%;
`;

const PdfSection = styled(InputSeparator)`
  flex: 1; /* Forces this section to grow and fill the vertical space */
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
`;

const PdfWidgetWrapper = styled.div`
  flex: 1; /* Forces the widget to stretch */
  width: 100%;
  border: 1px solid ${Palette.BORDER_OUTLINE};
  border-radius: 8px;
  overflow: hidden;
  background: ${Palette.GHOST_WHITE};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
`;

const PdfWidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: ${Palette.WHITE};
  border-bottom: 1px solid ${Palette.BORDER_OUTLINE};
  flex-shrink: 0;
`;

const PdfTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${Palette.BLACK};
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;

  svg {
    color: ${Palette.HAZY_BLUE};
  }
`;

const PdfExternalLink = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${Palette.HAZY_BLUE};
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const PdfFrame = styled.iframe`
  width: 100%;
  height: 100%; /* Takes up 100% of the stretched wrapper */
  flex: 1;
  border: none;
  display: block;
  background: ${Palette.GHOST_WHITE};
`;

const ConsentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 16px 12px;
  background: ${Palette.SOLITUDE};
  border-radius: 8px;
  border: 1px solid ${Palette.BORDER_OUTLINE};
  box-sizing: border-box;
  margin-top: 8px;
`;

const ConsentLabel = styled.span`
  color: ${Palette.BLACK};
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  line-height: 1.4;
  flex: 1;
  min-width: 0;
`;

function buildPdfViewerUrl(url) {
  if (!url) return url;
  const fitParams = 'view=FitH&toolbar=0&navpanes=0';
  if (url.includes('#')) {
    return url.includes('view=') ? url : `${url}&${fitParams}`;
  }
  return `${url}#${fitParams}`;
}

function AddBookingFormConsentStepV2({ t, control, errors, amenity }) {
  const waiverForm = amenity?.waiver_form;
  const pdfViewerUrl = useMemo(
    () => buildPdfViewerUrl(waiverForm?.url),
    [waiverForm?.url]
  );

  if (!waiverForm?.url) {
    return null;
  }

  const consentLabel = t('book_amenity_form_consent_label', {
    defaultValue:
      'On behalf of the resident, I consent to the terms and conditions outlined in the document above.'
  });

  return (
    <StepWrapper>
      <PdfSection>
        <FieldLabel>{t('Required Form')}</FieldLabel>
        <PdfWidgetWrapper>
          <PdfWidgetHeader>
            <PdfTitleGroup>
              <IoDocumentTextOutline size={18} />
              {waiverForm.name || 'Consent Form'}
            </PdfTitleGroup>
            <PdfExternalLink href={waiverForm.url} target="_blank" rel="noopener noreferrer">
              {t('Open Full Screen')}
              <IoOpenOutline size={14} />
            </PdfExternalLink>
          </PdfWidgetHeader>
          <PdfFrame src={pdfViewerUrl} title={waiverForm.name} />
        </PdfWidgetWrapper>
      </PdfSection>

      <InputSeparator style={{ marginTop: '16px', flexShrink: 0 }}>
        <Controller
          control={control}
          name="reservationFormConsent"
          render={({ field: { onChange, value } }) => (
            <ConsentRow>
              <ScheduleDayToggleButton
                type="button"
                aria-label={consentLabel}
                aria-pressed={!!value}
                onClick={() => onChange(!value)}>
                <ScheduleDayToggleMark $checked={!!value}>
                  {value ? <IoCheckmark color={Palette.WHITE} size={14} aria-hidden /> : null}
                </ScheduleDayToggleMark>
              </ScheduleDayToggleButton>
              <ConsentLabel>{consentLabel}</ConsentLabel>
            </ConsentRow>
          )}
        />
        {errors.reservationFormConsent && (
          <ErrorText>{errors.reservationFormConsent.message}</ErrorText>
        )}
      </InputSeparator>
    </StepWrapper>
  );
}

export default AddBookingFormConsentStepV2;
